import React, { useEffect, useState } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { recruitErrorMessage } from './shared/recruitUi';

const { Text } = Typography;
const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract', 'Temporary', 'Permanent', 'Intern'];
const CURRENCIES = ['AED', 'SAR', 'USD', 'EUR', 'INR', 'GBP'];
/** Editable while Published; everything else needs the job unpublished first. */
const PUBLISHED_EDITABLE = new Set([
  'description',
  'requirements',
  'closing_date',
  'recruiter_employee_id',
  'hiring_manager_employee_id',
  'location_text',
  'questions_json',
]);

const JobFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const [sp] = useSearchParams();
  const prefillRequisitionId = sp.get('requisition_id') || undefined;
  const navigate = useNavigate();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const { data: departments = [] } = useDepartmentsForSelection();
  const { data: positions = [] } = useQuery({ queryKey: hrmsKeys.positions(), queryFn: () => hrmsService.listPositions() });
  const { data: grades = [] } = useQuery({ queryKey: hrmsKeys.grades(), queryFn: () => hrmsService.listGrades() });
  const { data: requisitions = [] } = useQuery({
    queryKey: hrmsKeys.requisitions({ status: 'Open' }),
    queryFn: () => hrmsService.listRequisitions({ status: 'Open' }),
  });
  const { data: existing, isLoading } = useQuery({
    queryKey: hrmsKeys.job(id || ''),
    queryFn: () => hrmsService.getRecruitJob(id!),
    enabled: isEdit,
  });

  const status: string = existing?.status || 'Draft';
  const published = isEdit && status === 'Published';
  const closed = isEdit && status === 'Closed';
  const lockedField = (name: string) => closed || (published && !PUBLISHED_EDITABLE.has(name));

  const applyRequisition = (reqId?: string) => {
    const r = requisitions.find((x: any) => x.id === reqId);
    if (!r) return;
    const cur = form.getFieldsValue();
    form.setFieldsValue({
      title: cur.title || r.job_title,
      department_id: cur.department_id || r.department_id,
      position_id: cur.position_id || r.position_id,
      grade_id: cur.grade_id || r.grade_id,
      openings: Math.max(1, (r.headcount || 1) - (r.filled_count || 0)),
      location_text: cur.location_text || r.location_text,
      requirements: cur.requirements || r.required_skills,
      recruiter_employee_id: cur.recruiter_employee_id || r.recruiter_employee_id,
      hiring_manager_employee_id: cur.hiring_manager_employee_id || r.hiring_manager_employee_id,
      currency: r.currency || cur.currency,
    });
  };

  useEffect(() => {
    if (existing) {
      form.setFieldsValue({
        ...existing,
        closing_date: existing.closing_date ? dayjs(existing.closing_date) : undefined,
        salary_min: existing.salary_min != null ? Number(existing.salary_min) : undefined,
        salary_max: existing.salary_max != null ? Number(existing.salary_max) : undefined,
        questions_json: existing.questions_json || [],
      });
    }
  }, [existing, form]);

  useEffect(() => {
    if (!isEdit && prefillRequisitionId && requisitions.length) {
      form.setFieldValue('requisition_id', prefillRequisitionId);
      applyRequisition(prefillRequisitionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, prefillRequisitionId, requisitions.length]);

  const save = async () => {
    try {
      const v = await form.validateFields();
      const body: Record<string, unknown> = {
        ...v,
        closing_date: v.closing_date ? v.closing_date.format('YYYY-MM-DD') : null,
        questions_json: (v.questions_json || []).filter((q: any) => q?.text),
      };
      if (published) for (const k of Object.keys(body)) if (!PUBLISHED_EDITABLE.has(k)) delete body[k];
      setSaving(true);
      const saved = isEdit ? await hrmsService.updateRecruitJob(id!, body) : await hrmsService.createRecruitJob(body);
      message.success(isEdit ? 'Job updated' : 'Job posting saved as draft');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.jobs() });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.job(saved.id) });
      navigate(`/hr/jobs/${saved.id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(recruitErrorMessage(e, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  if (isEdit && isLoading) {
    return (
      <WorkspaceLayout header={<EnterprisePageHeader documentLabel="Job" />}>
        <Spin />
      </WorkspaceLayout>
    );
  }

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel={isEdit ? `Edit job ${existing?.job_number || ''}` : 'New job posting'}
          subtitle="Internal and/or careers channel — publish when ready"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(isEdit ? `/hr/jobs/${id}` : '/hr/jobs')}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} disabled={closed} onClick={save}>
                {isEdit ? 'Save changes' : 'Save draft'}
              </Button>
            </Space>
          }
        />
      }
    >
      {published && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16, maxWidth: 960 }}
          message="This job is live. You can edit the description, requirements, closing date, location, owners and screening questions. Unpublish it to change title, pay or openings."
        />
      )}
      {closed && <Alert type="warning" showIcon style={{ marginBottom: 16, maxWidth: 960 }} message="Closed jobs cannot be edited." />}
      <Form
        form={form}
        layout="vertical"
        disabled={closed}
        initialValues={{ openings: 1, channel: 'Both', employment_type: 'Full-time', salary_visibility: 'Hidden', currency: 'AED', questions_json: [] }}
        style={{ maxWidth: 960 }}
      >
        <Card title="Role" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="requisition_id" label="Linked requisition" extra="Defaults department, openings and owners from the requisition">
                <Select
                  allowClear
                  showSearch
                  disabled={lockedField('requisition_id')}
                  optionFilterProp="label"
                  placeholder="Select open requisition"
                  onChange={(v) => applyRequisition(v)}
                  options={requisitions.map((r: any) => ({ value: r.id, label: `${r.requisition_number} — ${r.job_title}` }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="title" label="Job title" rules={[{ required: true, message: 'Title is required' }]}>
                <Input placeholder="e.g. Senior Electrician" disabled={lockedField('title')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="department_id" label="Department">
                <Select
                  allowClear
                  showSearch
                  disabled={lockedField('department_id')}
                  optionFilterProp="label"
                  options={departments.map((d: any) => ({ value: d.id, label: d.department_name || d.name }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="position_id" label="Position">
                <Select
                  allowClear
                  showSearch
                  disabled={lockedField('position_id')}
                  optionFilterProp="label"
                  options={positions.map((p: any) => ({ value: p.id, label: p.position_name || p.name || p.position_code }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="grade_id" label="Grade">
                <Select
                  allowClear
                  showSearch
                  disabled={lockedField('grade_id')}
                  optionFilterProp="label"
                  options={grades.map((g: any) => ({ value: g.id, label: g.grade_name || g.name || g.grade_code }))}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="employment_type" label="Employment type">
                <Select disabled={lockedField('employment_type')} options={EMPLOYMENT_TYPES.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="channel" label="Channel">
                <Select
                  disabled={lockedField('channel')}
                  options={[
                    { value: 'Both', label: 'Careers + internal' },
                    { value: 'Careers', label: 'Careers site only' },
                    { value: 'Internal', label: 'Internal only' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="openings" label="Openings" rules={[{ required: true }]}>
                <InputNumber min={1} style={{ width: '100%' }} disabled={lockedField('openings')} />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="closing_date" label="Closing date" extra="Applications stop after this date">
                <DatePicker
                  style={{ width: '100%' }}
                  format="DD MMM YYYY"
                  disabledDate={(d) => d && d < dayjs().startOf('day')}
                  disabled={lockedField('closing_date')}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="location_text" label="Location">
                <Input disabled={lockedField('location_text')} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="slug"
                label="Careers URL slug"
                extra="Leave empty to generate from the title"
                rules={[{ pattern: /^[a-z0-9-]*$/, message: 'Lowercase letters, numbers and dashes only' }]}
              >
                <Input placeholder="senior-electrician" disabled={isEdit && status !== 'Draft'} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Pay" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={6}>
              <Form.Item name="salary_visibility" label="Show salary on careers site">
                <Select
                  disabled={lockedField('salary_visibility')}
                  options={[
                    { value: 'Hidden', label: 'Hidden' },
                    { value: 'Range', label: 'Show range' },
                    { value: 'Exact', label: 'Show amount' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={8} md={6}>
              <Form.Item name="currency" label="Currency">
                <Select disabled={lockedField('currency')} options={CURRENCIES.map((v) => ({ value: v, label: v }))} />
              </Form.Item>
            </Col>
            <Col xs={8} md={6}>
              <Form.Item name="salary_min" label="Monthly — min">
                <InputNumber min={0} style={{ width: '100%' }} disabled={lockedField('salary_min')} />
              </Form.Item>
            </Col>
            <Col xs={8} md={6}>
              <Form.Item name="salary_max" label="Monthly — max">
                <InputNumber min={0} style={{ width: '100%' }} disabled={lockedField('salary_max')} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Description" style={{ marginBottom: 16 }}>
          <Form.Item name="description" label="About the role">
            <Input.TextArea rows={6} />
          </Form.Item>
          <Form.Item name="requirements" label="Requirements">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Card>

        <Card title="Owners" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="recruiter_employee_id" label="Recruiter" extra="New applications are assigned to and notify this person">
                <RecruitPeoplePicker />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="hiring_manager_employee_id" label="Hiring manager">
                <RecruitPeoplePicker />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Screening questions"
          extra={<Text type="secondary">Shown on the public application form</Text>}
        >
          <Form.List name="questions_json">
            {(fields, { add, remove }) => (
              <>
                {fields.map((f) => (
                  <Row gutter={8} key={f.key} align="middle">
                    <Col flex="auto">
                      <Form.Item name={[f.name, 'text']} rules={[{ required: true, message: 'Question text' }]} style={{ marginBottom: 8 }}>
                        <Input placeholder="e.g. Do you hold a valid UAE driving licence?" />
                      </Form.Item>
                    </Col>
                    <Col flex="140px">
                      <Form.Item name={[f.name, 'type']} initialValue="text" style={{ marginBottom: 8 }}>
                        <Select
                          options={[
                            { value: 'text', label: 'Text' },
                            { value: 'yesno', label: 'Yes / No' },
                            { value: 'number', label: 'Number' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col flex="110px">
                      <Form.Item name={[f.name, 'required']} valuePropName="checked" style={{ marginBottom: 8 }}>
                        <Checkbox>Required</Checkbox>
                      </Form.Item>
                    </Col>
                    <Col flex="40px">
                      <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(f.name)} style={{ marginBottom: 8 }} />
                    </Col>
                  </Row>
                ))}
                <Button icon={<PlusOutlined />} onClick={() => add({ type: 'text', required: false })} disabled={closed}>
                  Add question
                </Button>
              </>
            )}
          </Form.List>
        </Card>
      </Form>
    </WorkspaceLayout>
  );
};

export default JobFormPage;
